import cx from 'classnames'
import { createElement, useContext, useEffect, useMemo } from 'react'

import { PrintShopFormContext } from '@lib/print/print-shop-form-context'
import { ShopFormMode } from '@lib/print/types'
import { UserContext } from '@lib/user/context'

import ShopFormProgressIndicator from './progress-indicator'

interface ShopFormProps {
  mode: ShopFormMode
  className?: string
}

const ShopForm = ({ mode, className }: ShopFormProps) => {
  const {
    activeStepIndex,
    shopFormSteps,
    shopState,
    setShopFormMode,
    dispatchShopState,
  } = useContext(PrintShopFormContext)
  const { user } = useContext(UserContext)

  const hasShopEditPermission = useMemo(() => {
    // Allow edit access if shop doesn't have an email (e.g., shop details step isn't finished)
    if (!shopState.userEmail) {
      return true
    }

    // Allow edit access if shop email matches logged in user email
    if (user && user.isLoggedIn && user.email === shopState.userEmail) {
      return true
    }

    return false
  }, [shopState, user])

  // Set shop form mode on load
  useEffect(() => {
    setShopFormMode(mode)
  }, [])

  // Scroll to top when step index changes
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }, [activeStepIndex])

  // Set shop email
  useEffect(() => {
    if (!user || !user.email) {
      return
    }

    dispatchShopState({
      action: 'updateShopEmail',
      email: user.email,
    })
  }, [dispatchShopState, user])

  if (!hasShopEditPermission) {
    return null
  }

  return (
    <div className={cx('space-y-8', className)}>
      <ShopFormProgressIndicator />

      <div className="flex items-start overflow-hidden">
        {shopFormSteps.map((shopFormStep, index) => {
          const element = createElement(
            shopFormStep.component,
            shopFormStep.props,
          )

          return (
            <div
              key={index}
              className={cx('transition-all duration-300', {
                'z-10': index === activeStepIndex,
              })}
              style={{
                flex: '0 0 100%',
                transform: `translateX(${-activeStepIndex * 100}%)`,
              }}
            >
              {element}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default ShopForm
